import _slicedToArray from "/vercel/path0/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "/vercel/path0/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { EnvironmentType } from '@Builder/dto/internal/env';
import { ActionTypes } from '@Builder/dto/internal/menuAction';
import useGetFlow from '@Builder/logic/hooks/useGetFlowFromId';
import useUpdateNodeCode from '@Builder/logic/hooks/useUpdateNodeCode';
import useUpdateResponse from '@Builder/logic/hooks/useUpdateResponse';
import { fromShallowToSpecificType, fromSpecificToShallowType, isValidHookType, makeEditorId } from '@Builder/utils/Validation';
import { useEffect, useState } from 'react';

function EditorContextHook() {
  var _useState = useState({}),
      editorData = _useState[0],
      setEditorData = _useState[1];

  var _useState2 = useState({}),
      activeEditor = _useState2[0],
      setActiveEditor = _useState2[1]; // --------
  // These are readonly states, so we don't/can't use use it to set the state


  var _useState3 = useState(EnvironmentType.DEVELOPMENT),
      flowEnvironment = _useState3[0],
      setFlowEnvironment = _useState3[1]; // this hook should _not_ care about activityId. Should encapsulate
  // that within the `useGetFlow` hook or otherwise make this unit independent..


  var _useState4 = useState(undefined),
      activityId = _useState4[0],
      setActivityId = _useState4[1]; // --------


  var _useGetFlow = useGetFlow(activityId),
      logicFlow = _useGetFlow.data;

  function updateEditorContent(nodeId, type) {
    var key = makeEditorId(nodeId, type);
    var node = editorData[key];

    if (node) {
      var serverCode = getServerCode(nodeId, type);
      setEditorData(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, key, _objectSpread(_objectSpread({}, node), {}, {
          hasServerChanges: false,
          editorContent: serverCode,
          hash: getServerHash(type, nodeId)
        })));
      });
    }
  }

  var nodes = function nodes() {
    var _ref2, _ref3;

    var _ref = (_ref2 = (_ref3 = logicFlow === null || logicFlow === void 0 ? void 0 : logicFlow.snap) !== null && _ref3 !== void 0 ? _ref3 : logicFlow) !== null && _ref2 !== void 0 ? _ref2 : {},
        logic = _ref.logic;

    return logic;
  };

  useEffect(function () {
    var updated = _objectSpread({}, editorData);

    Object.entries(updated).forEach(function (_ref4) {
      var _ref5 = _slicedToArray(_ref4, 2),
          key = _ref5[0],
          value = _ref5[1];

      var hasServerChanges = value.editorContent !== getServerCode(value.nodeId, value.type) && value.hash !== getServerHash(value.type, value.nodeId);
      updated[key] = _objectSpread(_objectSpread({}, value), {}, {
        hash: hasServerChanges ? value.hash : getServerHash(value.type, value.nodeId),
        hasServerChanges: hasServerChanges
      });
    });
    setEditorData(updated);
  }, [logicFlow]);

  function getCodeForNodeByActionType(type, nodeId) {
    var nodesByEnvironment = nodes(); // Trigger nodes have no id

    if (!nodeId) {
      var _nodesByEnvironment$f2;

      if (isValidHookType(type)) {
        var _type$split, _nodesByEnvironment$f;

        var _ref6 = (_type$split = type === null || type === void 0 ? void 0 : type.split('-')) !== null && _type$split !== void 0 ? _type$split : [],
            _ref7 = _slicedToArray(_ref6, 2),
            hookType = _ref7[0],
            codeType = _ref7[1];

        var _code = (_nodesByEnvironment$f = nodesByEnvironment[flowEnvironment]) === null || _nodesByEnvironment$f === void 0 ? void 0 : _nodesByEnvironment$f.hooks[hookType];

        return codeType === 'edit' ? _code.exec : _code[codeType];
      }

      var code = (_nodesByEnvironment$f2 = nodesByEnvironment[flowEnvironment]) === null || _nodesByEnvironment$f2 === void 0 ? void 0 : _nodesByEnvironment$f2.response;
      return code;
    }

    var node = getFlowNode(nodeId);
    var codeMap = getNodeMap(node, {
      execKey: 'exec',
      sampleKey: 'sample',
      inputKey: 'input',
      responseKey: 'response'
    });
    return codeMap[type];
  }

  function getUpdatedAtForNodeByActionType(nodeId, type) {
    var _nodesByEnvironment$f3;

    var nodesByEnvironment = nodes();
    var node = getFlowNode(nodeId);
    var codeMap = getNodeMap(node, {
      execKey: 'execUpdatedAt',
      sampleKey: 'sampleUpdatedAt',
      inputKey: 'inputUpdatedAt',
      responseKey: 'responseUpdatedAt'
    });
    return codeMap !== null && codeMap !== void 0 && codeMap[type] ? codeMap[type] : (_nodesByEnvironment$f3 = nodesByEnvironment[flowEnvironment]) === null || _nodesByEnvironment$f3 === void 0 ? void 0 : _nodesByEnvironment$f3.updatedAt;
  }

  function getNodeMap(node, _ref8) {
    var _node$interpret, _node$interpret2, _node$interpret3, _node$hooks, _node$hooks$before, _node$hooks2, _node$hooks2$after, _node$hooks3, _node$hooks3$onError, _node$hooks4, _node$hooks4$before, _node$hooks5, _node$hooks5$after, _node$hooks6, _node$hooks6$onError, _node$hooks7, _node$hooks7$before, _node$hooks8, _node$hooks8$after, _node$hooks9, _node$hooks9$onError, _ref9;

    var execKey = _ref8.execKey,
        sampleKey = _ref8.sampleKey,
        inputKey = _ref8.inputKey,
        responseKey = _ref8.responseKey;
    return _ref9 = {}, _defineProperty(_ref9, ActionTypes.EDIT, node === null || node === void 0 ? void 0 : (_node$interpret = node.interpret) === null || _node$interpret === void 0 ? void 0 : _node$interpret[execKey]), _defineProperty(_ref9, ActionTypes.SAMPLE, node === null || node === void 0 ? void 0 : (_node$interpret2 = node.interpret) === null || _node$interpret2 === void 0 ? void 0 : _node$interpret2[sampleKey]), _defineProperty(_ref9, ActionTypes.INPUT, node === null || node === void 0 ? void 0 : (_node$interpret3 = node.interpret) === null || _node$interpret3 === void 0 ? void 0 : _node$interpret3[inputKey]), _defineProperty(_ref9, ActionTypes.BEFORE_INPUT, node === null || node === void 0 ? void 0 : (_node$hooks = node.hooks) === null || _node$hooks === void 0 ? void 0 : (_node$hooks$before = _node$hooks.before) === null || _node$hooks$before === void 0 ? void 0 : _node$hooks$before[inputKey]), _defineProperty(_ref9, ActionTypes.AFTER_INPUT, node === null || node === void 0 ? void 0 : (_node$hooks2 = node.hooks) === null || _node$hooks2 === void 0 ? void 0 : (_node$hooks2$after = _node$hooks2.after) === null || _node$hooks2$after === void 0 ? void 0 : _node$hooks2$after[inputKey]), _defineProperty(_ref9, ActionTypes.ONERROR_INPUT, node === null || node === void 0 ? void 0 : (_node$hooks3 = node.hooks) === null || _node$hooks3 === void 0 ? void 0 : (_node$hooks3$onError = _node$hooks3.onError) === null || _node$hooks3$onError === void 0 ? void 0 : _node$hooks3$onError[inputKey]), _defineProperty(_ref9, ActionTypes.BEFORE_RESPONSE, node === null || node === void 0 ? void 0 : (_node$hooks4 = node.hooks) === null || _node$hooks4 === void 0 ? void 0 : (_node$hooks4$before = _node$hooks4.before) === null || _node$hooks4$before === void 0 ? void 0 : _node$hooks4$before[responseKey]), _defineProperty(_ref9, ActionTypes.AFTER_RESPONSE, node === null || node === void 0 ? void 0 : (_node$hooks5 = node.hooks) === null || _node$hooks5 === void 0 ? void 0 : (_node$hooks5$after = _node$hooks5.after) === null || _node$hooks5$after === void 0 ? void 0 : _node$hooks5$after[responseKey]), _defineProperty(_ref9, ActionTypes.ONERROR_RESPONSE, node === null || node === void 0 ? void 0 : (_node$hooks6 = node.hooks) === null || _node$hooks6 === void 0 ? void 0 : (_node$hooks6$onError = _node$hooks6.onError) === null || _node$hooks6$onError === void 0 ? void 0 : _node$hooks6$onError[responseKey]), _defineProperty(_ref9, ActionTypes.BEFORE_EDIT, node === null || node === void 0 ? void 0 : (_node$hooks7 = node.hooks) === null || _node$hooks7 === void 0 ? void 0 : (_node$hooks7$before = _node$hooks7.before) === null || _node$hooks7$before === void 0 ? void 0 : _node$hooks7$before[execKey]), _defineProperty(_ref9, ActionTypes.AFTER_EDIT, node === null || node === void 0 ? void 0 : (_node$hooks8 = node.hooks) === null || _node$hooks8 === void 0 ? void 0 : (_node$hooks8$after = _node$hooks8.after) === null || _node$hooks8$after === void 0 ? void 0 : _node$hooks8$after[execKey]), _defineProperty(_ref9, ActionTypes.ONERROR_EDIT, node === null || node === void 0 ? void 0 : (_node$hooks9 = node.hooks) === null || _node$hooks9 === void 0 ? void 0 : (_node$hooks9$onError = _node$hooks9.onError) === null || _node$hooks9$onError === void 0 ? void 0 : _node$hooks9$onError[execKey]), _ref9;
  }

  function getFlowNode(nodeId) {
    var _nodesByEnvironment$f4, _nodesByEnvironment$f5, _nodesByEnvironment$f6;

    var nodesByEnvironment = nodes();
    var node = nodesByEnvironment === null || nodesByEnvironment === void 0 ? void 0 : (_nodesByEnvironment$f4 = nodesByEnvironment[flowEnvironment]) === null || _nodesByEnvironment$f4 === void 0 ? void 0 : (_nodesByEnvironment$f5 = (_nodesByEnvironment$f6 = _nodesByEnvironment$f4.nodes).find) === null || _nodesByEnvironment$f5 === void 0 ? void 0 : _nodesByEnvironment$f5.call(_nodesByEnvironment$f6, function (_ref10) {
      var id = _ref10.id;
      return fromSpecificToShallowType(nodeId) === id;
    });
    return node;
  }

  function addEditing(node, type) {
    var id = makeEditorId(node.nodeId, type);
    setEditorData(function (current) {
      return _objectSpread(_objectSpread({}, current), {}, _defineProperty({}, id, {
        nodeId: node.nodeId,
        type: type,
        hash: getServerHash(type, node.nodeId),
        editorContent: getServerCode(node.nodeId, type),
        originalContent: getServerCode(node.nodeId, type)
      }));
    });
    setActiveEditor({
      id: node.nodeId,
      type: type
    });
  }

  function getEditorData(key) {
    return editorData[key];
  }

  function deleteEditorData(nodeId, type) {
    var key = "".concat(type, "-").concat(nodeId);

    var _editorData = _objectSpread({}, editorData);

    delete _editorData[key];
    setEditorData(_editorData);
  }

  function deleteAllEditorDataForNode(nodeId) {
    var updated = Object.entries(editorData).reduce(function (acc, _ref11) {
      var _ref12 = _slicedToArray(_ref11, 2),
          key = _ref12[0],
          value = _ref12[1];

      var codeEntries = _objectSpread({}, acc);

      if (value.nodeId !== nodeId) {
        codeEntries[key] = value;
      }

      return codeEntries;
    }, {});
    setEditorData(updated);
  }

  function clearEditor() {
    setEditorData({});
  }

  function getServerCode(nodeId, type) {
    return getCodeForNodeByActionType(type, nodeId);
  }

  function getServerHash(type, nodeId) {
    var nodesByEnvironment = nodes();

    if (!nodeId) {
      var _logicFlow$logic, _logicFlow$logic$deve;

      if (isValidHookType(type)) {
        var _type$split2, _nodesByEnvironment$f7;

        var _ref13 = (_type$split2 = type === null || type === void 0 ? void 0 : type.split('-')) !== null && _type$split2 !== void 0 ? _type$split2 : [],
            _ref14 = _slicedToArray(_ref13, 2),
            hookType = _ref14[0],
            codeType = _ref14[1];

        var code = (_nodesByEnvironment$f7 = nodesByEnvironment[flowEnvironment]) === null || _nodesByEnvironment$f7 === void 0 ? void 0 : _nodesByEnvironment$f7.hooks[hookType];
        return codeType === 'edit' ? code.execHash : code["".concat(codeType, "Hash")];
      }

      return logicFlow === null || logicFlow === void 0 ? void 0 : (_logicFlow$logic = logicFlow.logic) === null || _logicFlow$logic === void 0 ? void 0 : (_logicFlow$logic$deve = _logicFlow$logic.development) === null || _logicFlow$logic$deve === void 0 ? void 0 : _logicFlow$logic$deve.responseHash;
    }

    var node = getFlowNode(nodeId);
    var hashMap = getNodeMap(node, {
      execKey: 'execHash',
      sampleKey: 'sampleHash',
      inputKey: 'inputHash',
      responseKey: 'responseHash'
    });
    return hashMap[type];
  }

  function setChangedState(nodeId, type, editorContent) {
    var id = makeEditorId(nodeId, type);

    var updated = _objectSpread(_objectSpread({}, editorData), {}, _defineProperty({}, id, _objectSpread(_objectSpread({}, editorData[id]), {}, {
      editorContent: editorContent,
      hash: getServerHash(type, nodeId)
    })));

    setEditorData(updated);
  }

  function changesPending() {
    var changes = Object.values(editorData).filter(function (edited) {
      var og = getServerCode(edited.nodeId, edited.type);
      return edited.editorContent && edited.editorContent !== og;
    });
    return changes.length > 0;
  }

  var _useUpdateNodeCode = useUpdateNodeCode(),
      updateCode = _useUpdateNodeCode.updateCode;

  var _useUpdateResponse = useUpdateResponse(),
      updateResponse = _useUpdateResponse.updateResponse;

  function saveNode(_ref15) {
    var id = _ref15.id,
        type = _ref15.type,
        content = _ref15.content;
    // save editor content to context state
    setChangedState(id, type, content);
    var og = getServerCode(id, type);
    var flowId = fromShallowToSpecificType(logicFlow._id);

    if (og !== content) {
      if (type === 'response') {
        updateResponse({
          id: flowId,
          response: content
        });
      } else {
        // nodes of type 'edit' and 'input'.
        updateCode({
          id: flowId,
          nodeId: id,
          key: type,
          value: content
        });
      } // update editor data immediately


      var _key = makeEditorId(id, type);

      setEditorData(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, _key, _objectSpread(_objectSpread({}, prev[_key]), {}, {
          originalContent: content
        })));
      });
    }
  }

  return {
    // states
    activeEditor: activeEditor,
    editorData: editorData,
    // actions
    saveNode: saveNode,
    addEditing: addEditing,
    clearEditor: clearEditor,
    changesPending: changesPending,
    deleteEditorData: deleteEditorData,
    deleteAllEditorDataForNode: deleteAllEditorDataForNode,
    getEditorData: getEditorData,
    getFlowNode: getFlowNode,
    getServerCode: getServerCode,
    getCodeForNodeByActionType: getCodeForNodeByActionType,
    getUpdatedAtForNodeByActionType: getUpdatedAtForNodeByActionType,
    setChangedState: setChangedState,
    setFlowEnvironment: setFlowEnvironment,
    setActivityId: setActivityId,
    setActiveEditor: setActiveEditor,
    updateEditorContent: updateEditorContent
  };
}

export default EditorContextHook;